import { createEventSpaceList, addSpaceMembership } from '~/server/event/event-space-lists.service'
import type { EventSpaceList } from '~/shared/event/event.types'
import { SupportMode } from '~/shared/common/enums'
import { sendSlackWebhookClient } from '~/client/common/slack'
import { SlackChannel } from '~/shared/common/slack'
import { getMe } from '~/server/common/supabase/user'
import { fireCustomerIOEvent } from '~/client/common/customerio'
import { CustomerIOEventName } from '~/shared/customerio/customerio.types'

export interface CreateEventOptions {
  payload: Partial<EventSpaceList>
  spaceId?: number
  spaceIds?: number[]
  shouldAddSpaceOnCreate?: boolean
  /** Discovery surface that triggered the create (passed through to space_added_to_event). */
  source?: 'browse' | 'recs' | 'collection' | 'ghost' | 'manual'
}

export async function createEvent({ payload, spaceId, spaceIds, shouldAddSpaceOnCreate = true, source }: CreateEventOptions) {
  // 1. Create the event
  const created = await createEventSpaceList(payload)

  // 2. Determine which spaces to add
  const idsToAdd: number[] = []
  if (shouldAddSpaceOnCreate) {
    if (spaceIds && spaceIds.length > 0) {
      idsToAdd.push(...spaceIds)
    } else if (typeof spaceId === 'number') {
      idsToAdd.push(spaceId)
    }
  }

  // 3. Add space memberships
  let wasAlreadyAdded = false
  let spacesAdded = 0
  if (created?.id && idsToAdd.length > 0) {
    const results = await Promise.all(
      idsToAdd.map((id) =>
        addSpaceMembership({
          event_space_list_id: created.id!,
          space_id: id,
        })
      )
    )
    spacesAdded = results.filter((r) => !r.alreadyExists).length
    if (idsToAdd.length === 1) {
      wasAlreadyAdded = results[0].alreadyExists
    }
    results.forEach((r, idx) => {
      if (r.alreadyExists) return
      void fireCustomerIOEvent(CustomerIOEventName.SpaceAddedToEvent, {
        event_id: created.id,
        space_id: idsToAdd[idx],
        source: source ?? 'browse',
      })
    })
  }

  // 4a. Fire Customer.io `event_created`. Server-side handler resolves the
  // user and computes is_first via on-fire query against crm_eventspacelist.
  void fireCustomerIOEvent(CustomerIOEventName.EventCreated, {
    event_id: created?.id ?? null,
    event_name: payload.name ?? null,
    support_mode: payload.support_mode ?? null,
    location: payload.location ?? null,
    num_attendees: payload.num_attendees ?? null,
    spaces_added: spacesAdded,
  })

  // 4. Send Slack notification for event creation
  try {
    const me = await getMe()
    const { useAuthStore } = await import('~/client/common/stores/auth')
    const { viewAsUserDetail, userDetail: adminDetail } = useAuthStore.getState()
    const userDetail = viewAsUserDetail ?? adminDetail
    const userName = [userDetail?.first_name, userDetail?.last_name].filter(Boolean).join(' ')
    const userLabel = userName ? `${userName} (${me?.email || 'unknown'})` : (me?.email || 'unknown')
    await sendSlackWebhookClient({
      message: `New event created by ${userLabel}`,
      channel: SlackChannel.NEW_EVENT_CREATED,
      fields: Object.fromEntries(
        Object.entries({
          Event: [payload.name, created?.id && `(#${created.id})`].filter(Boolean).join(' '),
          Location: payload.location || '',
          Attendees: payload.num_attendees ? String(payload.num_attendees) : '',
          'Support Mode': payload.support_mode || '',
        }).filter(([, v]) => v)
      ),
      level: 'blue',
    })
  } catch (err) {
    console.error('Failed to send Slack notification for new event', err)
  }

  // 5. Send Slack notification if Concierge
  if (payload.support_mode === SupportMode.CONCIERGE) {
    try {
      const me = await getMe()
      const { useAuthStore: authStore } = await import('~/client/common/stores/auth')
      const { viewAsUserDetail: vas, userDetail: adminUd } = authStore.getState()
      const ud = vas ?? adminUd
      const conciergeUserName = [ud?.first_name, ud?.last_name].filter(Boolean).join(' ')
      const conciergeUserLabel = conciergeUserName ? `${conciergeUserName} (${me?.email || ''})` : (me?.email || '')
      const displayJson = payload.display_json as Record<string, any> | undefined
      await sendSlackWebhookClient({
        message: `Concierge event created by ${conciergeUserLabel}`,
        channel: SlackChannel.NEW_CONCIERGE_REQUEST,
        fields: Object.fromEntries(
          Object.entries({
            Event: [payload.name, created?.id && `(#${created.id})`].filter(Boolean).join(' '),
            Location: [payload.location, payload.location_max_distance && `within ${payload.location_max_distance} mi`].filter(Boolean).join(' · '),
            Attendees: [payload.num_attendees && String(payload.num_attendees), payload.event_type || payload.event_format].filter(Boolean).join(' · '),
            'Privacy Type': payload.privacy_requirement || '',
            'Space Type': Array.isArray(payload.space_type) ? payload.space_type.join(', ') : payload.space_type || '',
            'A/V Needed': Array.isArray(payload.media_requirements) ? payload.media_requirements.join(', ') : payload.media_requirements || '',
            'Amenities Needed': Array.isArray(payload.amenities_requirements) ? payload.amenities_requirements.join(', ') : payload.amenities_requirements || '',
            Budget: [payload.budget_total && `$${Number(payload.budget_total).toLocaleString()}`, displayJson?.budget_type].filter(Boolean).join(' · '),
            Details: payload.criteria || '',
          }).filter(([, v]) => v)
        ),
        level: 'blue',
      })
    } catch (err) {
      console.error('Failed to send Slack webhook for concierge event', err)
    }
  }

  return { event: created, wasAlreadyAdded, spacesAdded }
}
